<script setup lang="ts">
import { IPosts } from '@/models/Posts'
import { app } from '@/composition/app'
import { openGlobalUserProfile } from '@/components/helper'
import {
  ChevronLeftIcon,
  DotsHorizontalIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIDropdown,
  UITextMdMedium,
  UITextSmNormal,
  UIPopover,
  UISkeleton,
} from '@gohighlevel/ghl-ui'
import { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()
import PopoverAvatar from '../../common/avatar/PopoverAvatar.vue'
import UserProfilePopover from '@/components/common/popover/UserProfilePopover.vue'

import { GroupMemberRole } from '@/models/GroupMember'

const _props = defineProps({
  post: {
    type: Object as PropType<IPosts>,
    required: false,
    default: null,
  },
  channelSlug: {
    type: String,
    required: false,
  },
  isContentScrolled: {
    type: Boolean,
    required: false,
  },
  authorRole: {
    type: String as PropType<GroupMemberRole>,
    required: false,
  },
  channelName: {
    type: String,
    required: false,
  },
  postActionOptions: {
    type: Array as PropType<
      Array<{
        label: string
        key: string
        class: string
        icon: () => any
      }>
    >,
    required: false,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'user-click', userId: string): void
  (e: 'channel-click', channelSlug: string): void
  (e: 'action-selected', action: string): void
}>()

const route = useRoute()
const router = useRouter()

function onDropdownSelect(action: any) {
  const emitted = action?.key ?? action
  emit('action-selected', emitted)
}
</script>

<template>
  <div
    id="post_view_top__container"
    class="relative flex flex-row items-start justify-between bg-communities-background text-communities-font-secondary transition-shadow duration-200"
    :class="[
      app.state.isMobile
        ? 'fixed left-0 top-0 z-50 h-[61px] w-full border-b border-communities-border bg-white px-3 pb-3 pt-2 before:absolute before:inset-0 before:-z-10 before:bg-white before:content-[\'\']'
        : 'mx-3 mb-4',
      isContentScrolled && 'shadow-md',
    ]"
  >
    <!-- Loading State -->
    <div
      v-if="loading || !post"
      class="flex w-full items-center justify-between"
    >
      <div class="flex items-center space-x-3">
        <!-- Avatar Skeleton -->
        <UISkeleton height="40px" width="40px" circle />
        <!-- User Info Skeleton -->
        <div class="space-y-2">
          <UISkeleton height="16px" width="128px" />
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <template v-else>
      <div
        class="user-profile text-md relative flex w-full max-w-[90%] items-center justify-start"
      >
        <!-- Back Button -->
        <div
          class="backbutton mr-3 flex cursor-pointer items-center md:hidden"
          tabindex="0"
          role="button"
          :aria-label="t('common.ariaLabels.back')"
          @click="emit('back')"
          @keyup.enter="emit('back')"
        >
          <ChevronLeftIcon class="h-6 w-6 text-communities-font-secondary" />
        </div>

        <!-- Avatar -->
        <div class="cursor-pointer">
          <PopoverAvatar :user="post.user" :user-role="authorRole" />
        </div>

        <!-- User Info -->
        <div class="flex flex-col gap-[0.125rem] truncate px-3">
          <div class="flex w-full items-center truncate">
            <UIPopover
              placement="bottom-start"
              :show-arrow="false"
              raw
              :show="app.state.isMobile ? false : undefined"
            >
              <template #trigger>
                <UITextMdMedium
                  id="user_profile_tooltip_trigger_post_view"
                  :class="{
                    'hover:underline': post.user && post.user.fullName,
                  }"
                  class="line-clamp-1 cursor-pointer text-communities-font-primary"
                  @click.stop="
                    openGlobalUserProfile(post.user.slug, router, post.groupId)
                  "
                >
                  {{ (post.user && post.user.fullName) || 'Deleted user' }}
                </UITextMdMedium>
              </template>
              <UserProfilePopover
                id="user_profile_tooltip"
                class="w-[460px]"
                :user="post.user"
                :user-role="authorRole"
              />
            </UIPopover>
          </div>
          <div class="flex font-light text-communities-font-secondary">
            <UITextSmNormal
              class="whitespace-nowrap text-communities-font-secondary"
            >
              {{ post.relativeTime }}
            </UITextSmNormal>
            <UITextSmNormal
              v-if="!route.params?.channelSlug"
              class="whitespace-nowrap px-1 text-communities-font-secondary lg:block"
            >
              {{ t('common.in') }}
            </UITextSmNormal>
            <UITextSmNormal
              v-if="!route.params?.channelSlug"
              class="min-w-0 flex-shrink cursor-pointer truncate font-semibold text-communities-font-secondary hover:underline lg:block"
              @click="emit('channel-click', channelSlug || '')"
            >
              {{ channelName }}
            </UITextSmNormal>
          </div>
        </div>
      </div>

      <!-- Action Dropdown -->
      <UIDropdown
        id="post-action-dropdown"
        :options="postActionOptions"
        placement="bottom-end"
        class="rounded-3xl"
        @select="onDropdownSelect"
      >
        <div
          id="post-action-dropdown-trigger"
          class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-communities-surface"
          @click.stop=""
        >
          <DotsHorizontalIcon class="w-5 text-communities-font-secondary" />
        </div>
      </UIDropdown>
    </template>
  </div>
</template>
